import { Link } from 'react-router-dom'
import { 
  Clock, 
  Users, 
  FileText, 
  Calendar, 
  CheckSquare, 
  TrendingUp, 
  Shield, 
  Zap,
  ArrowRight,
  CheckCircle2,
  Menu,
  X
} from 'lucide-react'
import { useState } from 'react'

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const features = [
    {
      icon: Clock,
      title: 'Attendance & Timesheet',
      description: 'Sistem absensi otomatis dengan tracking waktu real-time. Kelola jam kerja karyawan dengan mudah dan akurat.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Calendar,
      title: 'Leave Management',
      description: 'Ajukan dan kelola cuti, izin, sakit dengan sistem approval yang transparan dan cepat.',
      color: 'from-blue-500 to-pink-500'
    },
    {
      icon: CheckSquare,
      title: 'Task Management',
      description: 'Kelola proyek dan tugas tim dengan board kanban, timeline, dan progress tracking.',
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: FileText,
      title: 'Document Generator',
      description: 'Buat CV profesional, surat lamaran, surat resign dengan template siap pakai.',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      description: 'Chat real-time, file sharing, dan kolaborasi tim dalam satu platform terintegrasi.',
      color: 'from-blue-500 to-blue-500'
    },
    {
      icon: TrendingUp,
      title: 'Analytics Dashboard',
      description: 'Dashboard analytics lengkap dengan insights produktivitas dan performa tim.',
      color: 'from-pink-500 to-rose-500'
    }
  ]

  const benefits = [
    'Sistem approval multi-level untuk kontrol yang lebih baik',
    'Notifikasi real-time untuk update penting',
    'Export data ke berbagai format (PDF, Excel, CSV)',
    'Mobile responsive - akses dari mana saja',
    'Role-based access control untuk keamanan data',
    'Integration ready dengan sistem lain'
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-blue-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-xl border-b border-gray-200 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <img 
                src="/assets/logos/logo-variant-4.png" 
                alt="RBApps" 
                className="h-10 w-auto"
              />
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent">
                RBApps HRM
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-700 hover:text-blue-600 transition font-medium">
                Fitur
              </a>
              <a href="#benefits" className="text-gray-700 hover:text-blue-600 transition font-medium">
                Keunggulan
              </a>
              <Link 
                to="/login" 
                className="text-gray-700 hover:text-blue-600 transition font-medium"
              >
                Login
              </Link>
              <Link 
                to="/register" 
                className="bg-gradient-to-r from-blue-600 to-blue-600 text-white px-6 py-2.5 rounded-xl font-medium hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                Daftar Sekarang
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col gap-4">
                <a 
                  href="#features" 
                  className="text-gray-700 hover:text-blue-600 transition font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Fitur
                </a>
                <a 
                  href="#benefits" 
                  className="text-gray-700 hover:text-blue-600 transition font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Keunggulan
                </a>
                <Link 
                  to="/login" 
                  className="text-gray-700 hover:text-blue-600 transition font-medium"
                >
                  Login
                </Link>
                <Link 
                  to="/register" 
                  className="bg-gradient-to-r from-blue-600 to-blue-600 text-white px-6 py-2.5 rounded-xl font-medium text-center"
                >
                  Daftar Sekarang
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Zap size={16} />
                Platform HR Management Modern
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Kelola <span className="bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent">Tim & HR</span> Lebih Efisien
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Platform all-in-one untuk manajemen karyawan, absensi, cuti, tugas, dan dokumen. 
                Tingkatkan produktivitas tim Anda hingga 10x lipat.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/register"
                  className="group bg-gradient-to-r from-blue-600 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  Mulai Gratis
                  <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
                </Link>
                <a 
                  href="#features"
                  className="bg-white border-2 border-gray-200 text-gray-700 px-8 py-4 rounded-xl font-semibold hover:border-blue-600 hover:text-blue-600 transition-all duration-300 text-center"
                >
                  Lihat Fitur
                </a>
              </div>
              
              <div className="mt-12 flex items-center gap-8">
                <div>
                  <div className="text-3xl font-bold text-gray-900">10+</div>
                  <div className="text-sm text-gray-600">Fitur Lengkap</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900">100%</div>
                  <div className="text-sm text-gray-600">Mobile Friendly</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900">24/7</div>
                  <div className="text-sm text-gray-600">Akses Anytime</div>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-400 rounded-3xl blur-3xl opacity-20"></div>
              <img 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop" 
                alt="Team Collaboration" 
                className="relative rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              Fitur <span className="bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent">Lengkap</span> untuk Bisnis Anda
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Semua yang Anda butuhkan untuk mengelola HR dan tim dalam satu platform terintegrasi
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200"
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${feature.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <feature.icon className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                
                <div className="mt-6 flex items-center text-blue-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  Pelajari lebih lanjut
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-pink-400 rounded-3xl blur-3xl opacity-20"></div>
              <img 
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&h=600&fit=crop" 
                alt="Dashboard Preview" 
                className="relative rounded-3xl shadow-2xl"
              />
            </div>

            {/* Content */}
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Kenapa Memilih <span className="bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent">RBApps HRM</span>?
              </h2>
              
              <p className="text-xl text-gray-600 mb-8">
                Platform yang dirancang khusus untuk memenuhi kebutuhan HR modern dengan teknologi terkini
              </p>

              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mt-1">
                      <CheckCircle2 className="text-green-600" size={16} />
                    </div>
                    <p className="text-gray-700 text-lg">{benefit}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-blue-50 rounded-2xl border border-blue-100">
                <div className="flex items-center gap-3 mb-2">
                  <Shield className="text-blue-600" size={24} />
                  <h3 className="font-bold text-lg text-gray-900">Keamanan Data Terjamin</h3>
                </div>
                <p className="text-gray-600">
                  Data karyawan Anda dilindungi dengan enkripsi tingkat enterprise dan backup otomatis
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Preview Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent">Preview</span> Aplikasi
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Contoh tampilan Timesheet Management - Login untuk mengakses fitur lengkap
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden relative">
            {/* Overlay untuk mencegah interaksi */}
            <div 
              className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white/95 z-10 cursor-not-allowed"
              onClick={(e) => {
                e.preventDefault()
                // Show toast notification
                const toast = document.createElement('div')
                toast.className = 'fixed top-4 right-4 bg-gradient-to-r from-blue-600 to-blue-600 text-white px-6 py-4 rounded-2xl shadow-2xl z-50 animate-slide-in'
                toast.innerHTML = `
                  <div class="flex items-center space-x-3">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <span class="font-semibold">Silakan login untuk menggunakan fitur ini</span>
                  </div>
                `
                document.body.appendChild(toast)
                setTimeout(() => {
                  toast.style.animation = 'slide-out 0.3s ease-in'
                  setTimeout(() => toast.remove(), 300)
                }, 3000)
              }}
            >
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center z-20 w-full px-4">
                <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md mx-auto border-2 border-blue-200">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Login Diperlukan</h3>
                  <p className="text-gray-600 mb-6">Login untuk mengakses semua fitur dan mengelola data Anda</p>
                  <Link
                    to="/login"
                    onClick={(e) => e.stopPropagation()}
                    className="block w-full px-6 py-3.5 bg-gradient-to-r from-blue-600 to-blue-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-blue-700 transform hover:scale-105 transition-all duration-200"
                  >
                    Login Sekarang
                  </Link>
                  <p className="mt-4 text-sm text-gray-500">
                    Belum punya akun? <Link to="/register" onClick={(e) => e.stopPropagation()} className="text-blue-600 hover:text-blue-700 font-medium">Daftar di sini</Link>
                  </p>
                </div>
              </div>
            </div>

            {/* Demo Preview Content */}
            <div className="p-8 pointer-events-none select-none opacity-50">
              {/* Header Demo */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-600 rounded-2xl p-8 mb-8 text-white">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <Clock className="w-8 h-8" />
                      <h3 className="text-3xl font-bold">Timesheet Management</h3>
                    </div>
                    <p className="text-blue-100">Kelola waktu kerja dan aktivitas harian Anda</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <input
                      type="month"
                      value="2025-12"
                      className="px-4 py-2.5 rounded-xl bg-white/20 border-2 border-white/30 text-white font-medium backdrop-blur-sm"
                      disabled
                    />
                    <button
                      className="px-6 py-2.5 bg-white text-blue-600 rounded-xl font-semibold shadow-lg"
                      disabled
                    >
                      + Tambah
                    </button>
                  </div>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-blue-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total Masuk</p>
                      <p className="text-3xl font-bold text-gray-900">0</p>
                    </div>
                    <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                      <CheckCircle2 className="w-7 h-7 text-blue-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-red-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total Sakit</p>
                      <p className="text-3xl font-bold text-gray-900">0</p>
                    </div>
                    <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center">
                      <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-yellow-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total Cuti</p>
                      <p className="text-3xl font-bold text-gray-900">0</p>
                    </div>
                    <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center">
                      <Calendar className="w-7 h-7 text-yellow-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Empty State */}
              <div className="bg-white rounded-2xl p-12 text-center border-2 border-dashed border-gray-300">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Belum ada catatan timesheet</h3>
                <p className="text-gray-600 mb-6">Mulai catat aktivitas harian Anda untuk tracking yang lebih baik</p>
                <button
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-600 text-white rounded-xl font-semibold shadow-lg"
                  disabled
                >
                  + Tambah Timesheet
                </button>
              </div>
            </div>
          </div>

          {/* Info Text */}
          <div className="mt-8 text-center">
            <p className="text-gray-600 text-lg">
              Ini hanya preview tampilan. <Link to="/login" className="text-blue-600 hover:text-blue-700 font-semibold">Login</Link> untuk mengakses fitur lengkap dengan data Anda.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Siap Tingkatkan Produktivitas Tim Anda?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Bergabunglah dengan ribuan perusahaan yang telah mempercayai RBApps HRM
          </p>
          <Link 
            to="/register"
            className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
          >
            Daftar Gratis Sekarang
            <ArrowRight size={20} />
          </Link>
          <p className="mt-4 text-blue-100 text-sm">
            Tidak perlu kartu kredit • Setup 5 menit • Support 24/7
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="/assets/logos/logo-variant-4.png" 
                  alt="RBApps" 
                  className="h-8 w-auto brightness-200"
                />
                <span className="text-lg font-bold text-white">RBApps HRM</span>
              </div>
              <p className="text-sm text-gray-400">
                Platform HR Management modern untuk bisnis Indonesia
              </p>
            </div>
            
            <div>
              <h4 className="font-bold text-white mb-4">Produk</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition">Fitur</a></li>
                <li><a href="#benefits" className="hover:text-white transition">Keunggulan</a></li>
                <li><Link to="/register" className="hover:text-white transition">Harga</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-white mb-4">Perusahaan</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => {}} className="hover:text-white transition">Tentang Kami</button></li>
                <li><button onClick={() => {}} className="hover:text-white transition">Karir</button></li>
                <li><button onClick={() => {}} className="hover:text-white transition">Blog</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-white mb-4">Dukungan</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => {}} className="hover:text-white transition">Help Center</button></li>
                <li><Link to="/login" className="hover:text-white transition">Login</Link></li>
                <li><Link to="/register" className="hover:text-white transition">Register</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2024 RBApps. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
